import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays the selected variant's discount message
 * (variant metafield `custom.discount_message`).
 *
 * Metafields are not included in the variant JSON of the variant update event,
 * so the message is read from the re-rendered section HTML instead.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} message - The span element that displays the message text
 *
 * @extends {Component<Refs>}
 */
class JffdDiscountMessage extends Component {
  requiredRefs = ['message'];

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateMessage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateMessage);
  }

  /**
   * Updates the discount message from the re-rendered section HTML.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateMessage = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const updatedMessage = event.detail.data.html.querySelector(
      `jffd-discount-message[data-block-id="${this.dataset.blockId}"]`
    );
    if (!updatedMessage) return;

    this.toggleAttribute('hidden', updatedMessage.hasAttribute('hidden'));
    this.refs.message.textContent = updatedMessage.textContent?.trim() ?? '';
  };
}

if (!customElements.get('jffd-discount-message')) {
  customElements.define('jffd-discount-message', JffdDiscountMessage);
}
